// GradesDashboardPage - Comprehensive admin grades dashboard
// Multi-select filters, Moy. par Classe by matiere (expandable to students),
// Par Etudiant (expandable to classes), Excel/PDF export.

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  TrendingUp,
  BarChart3,
  RotateCcw,
  XCircle,
  Search,
  Loader2,
  Download,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Edit3,
  Users,
  BookOpen,
  GraduationCap,
  FileText,
} from 'lucide-react';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { Permission } from '@/types/global.types';
import {
  getAdminGradeFilters,
  getGradeAnalytics,
  getGradeOverview,
  getStudentStatusReport,
  getMatiereSummary,
  getStudentGradeDetail,
} from '@/services/adminGradesService';
import type {
  AdminGradeFilters,
  AdminGradeFiltersResponse,
  GradeOverviewRow,
  StudentStatusRow,
  GradeAnalyticsSummary,
  MatiereGradeSummary,
  StudentGradeDetail,
} from '@/types/admin-grades.types';
import { GradeOverrideModal, GradeExportModal, StudentGradeReportModal, GradeTrackingModal } from '../components/grades';
import type { ComponentInfo } from '../components/grades/GradeOverrideModal';

// ==================== TYPES ====================

type TabKey = 'averages' | 'students';

interface OverrideTarget {
  studentId: string;
  groupeId: string;
  studentName: string;
  moduleElementName: string;
  components: ComponentInfo[];
}

// ==================== HELPERS ====================

function getStatusBadge(status: string): { label: string; classes: string } {
  switch (status) {
    case 'VALIDATED':
      return { label: 'Valide', classes: 'bg-green-100 text-green-700' };
    case 'FAILED':
      return { label: 'Echoue', classes: 'bg-red-100 text-red-700' };
    case 'RATTRAPAGE':
      return { label: 'Rattrapage', classes: 'bg-amber-100 text-amber-700' };
    case 'INCOMPLETE':
      return { label: 'Incomplet', classes: 'bg-gray-100 text-gray-600' };
    default:
      return { label: status, classes: 'bg-gray-100 text-gray-600' };
  }
}

function getStudentStatusBadge(status: string): { label: string; classes: string } {
  switch (status) {
    case 'EXCELLENT':
      return { label: 'Excellent', classes: 'bg-green-100 text-green-700' };
    case 'PASSING':
      return { label: 'Admis', classes: 'bg-blue-100 text-blue-700' };
    case 'AT_RISK':
      return { label: 'A risque', classes: 'bg-amber-100 text-amber-700' };
    case 'FAILING':
      return { label: 'En echec', classes: 'bg-red-100 text-red-700' };
    default:
      return { label: status, classes: 'bg-gray-100 text-gray-600' };
  }
}

function getAverageColor(avg: number | null): string {
  if (avg === null) return 'text-gray-500';
  if (avg >= 12) return 'text-green-600';
  if (avg >= 10) return 'text-amber-600';
  return 'text-red-600';
}

// ==================== TAB DEFINITIONS ====================

const TABS: { key: TabKey; label: string }[] = [
  { key: 'averages', label: 'Moy. par Classe' },
  { key: 'students', label: 'Par Etudiant' },
];

// ==================== MULTI-SELECT FILTER ====================

interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectFilterProps {
  label: string;
  allLabel: string;
  options: MultiSelectOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
}

function MultiSelectFilter({ label, allLabel, options, selected, onChange }: MultiSelectFilterProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const allSelected = selected.length === options.length && options.length > 0;

  const toggleAll = () => {
    onChange(allSelected ? [] : options.map((o) => o.value));
  };

  const toggleOption = (value: string) => {
    onChange(
      selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value]
    );
  };

  const buttonLabel = selected.length === 0 ? allLabel : `${selected.length} selectionnes`;

  return (
    <div ref={ref} className="relative">
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between border border-gray-300 rounded-lg px-4 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-left"
      >
        <span className="truncate">{buttonLabel}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 ml-2 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          <label className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={toggleAll}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">
              {allSelected ? 'Tout deselectionner' : 'Tout selectionner'}
            </span>
          </label>
          {options.map((opt) => (
            <label
              key={opt.value}
              className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selected.includes(opt.value)}
                onChange={() => toggleOption(opt.value)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">{opt.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ==================== MAIN COMPONENT ====================

const GradesDashboardPage: React.FC = () => {
  // Permission check for manage/export actions
  const { hasPermission } = usePermissions();
  // Grade overrides require GRADES_MANAGE server-side — gate the UI on the
  // same permission so the Modifier action never shows when the call would 403
  const canManage = hasPermission(Permission.GRADES_MANAGE);

  // Filter data
  const [filtersData, setFiltersData] = useState<AdminGradeFiltersResponse | null>(null);
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [filtersError, setFiltersError] = useState<string | null>(null);

  // Selected filter values (multi-select)
  const [selectedCampusIds, setSelectedCampusIds] = useState<string[]>([]);
  const [selectedAcademicYearId, setSelectedAcademicYearId] = useState<string>('');
  const [selectedSemesterNumbers, setSelectedSemesterNumbers] = useState<number[]>([]);
  const [selectedFiliereIds, setSelectedFiliereIds] = useState<string[]>([]);
  const [selectedNiveaux, setSelectedNiveaux] = useState<number[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Active tab
  const [activeTab, setActiveTab] = useState<TabKey>('averages');

  // Analytics
  const [analytics, setAnalytics] = useState<GradeAnalyticsSummary | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // Tab data
  const [matiereData, setMatiereData] = useState<MatiereGradeSummary[] | null>(null);
  const [matiereLoading, setMatiereLoading] = useState(false);
  const [overviewData, setOverviewData] = useState<GradeOverviewRow[] | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(false);

  const [studentsData, setStudentsData] = useState<StudentStatusRow[] | null>(null);
  const [studentsLoading, setStudentsLoading] = useState(false);

  // Data error
  const [dataError, setDataError] = useState<string | null>(null);

  // Expand state
  const [expandedMatiereKey, setExpandedMatiereKey] = useState<string | null>(null);
  const [expandedStudentId, setExpandedStudentId] = useState<string | null>(null);
  const [studentDetail, setStudentDetail] = useState<StudentGradeDetail[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);

  // Override modal
  const [overrideTarget, setOverrideTarget] = useState<OverrideTarget | null>(null);

  // Export modals
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [gradeTrackingOpen, setGradeTrackingOpen] = useState(false);
  const [studentReportModal, setStudentReportModal] = useState<{
    open: boolean;
    studentId: string;
    studentName: string;
    studentCode: string;
  }>({ open: false, studentId: '', studentName: '', studentCode: '' });

  // Build current filter params
  const currentFilters: AdminGradeFilters = useMemo(() => ({
    campusIds: selectedCampusIds.length > 0 ? selectedCampusIds : undefined,
    academicYearId: selectedAcademicYearId || undefined,
    semesterNumbers: selectedSemesterNumbers.length > 0 ? selectedSemesterNumbers : undefined,
    filiereIds: selectedFiliereIds.length > 0 ? selectedFiliereIds : undefined,
    niveaux: selectedNiveaux.length > 0 ? selectedNiveaux : undefined,
  }), [selectedCampusIds, selectedAcademicYearId, selectedSemesterNumbers, selectedFiliereIds, selectedNiveaux]);

  // Filter summary for export modal
  const filterSummary = useMemo(() => {
    const fd = filtersData;
    return {
      campusNames: selectedCampusIds.length > 0
        ? fd?.campuses?.filter((c) => selectedCampusIds.includes(c.id)).map((c) => c.name).join(', ') || 'Tous les campus'
        : 'Tous les campus',
      academicYearName: fd?.academicYears?.find((y) => y.id === selectedAcademicYearId)?.name || '-',
      semesters: selectedSemesterNumbers.length > 0
        ? selectedSemesterNumbers.map((n) => `Semestre ${n}`).join(', ')
        : 'Tous les semestres',
      filieres: selectedFiliereIds.length > 0
        ? fd?.filieres?.filter((f) => selectedFiliereIds.includes(f.id)).map((f) => f.name).join(', ') || 'Toutes les filieres'
        : 'Toutes les filieres',
      niveaux: selectedNiveaux.length > 0
        ? selectedNiveaux.map((n) => `Niveau ${n}`).join(', ')
        : 'Tous les niveaux',
    };
  }, [filtersData, selectedCampusIds, selectedAcademicYearId, selectedSemesterNumbers, selectedFiliereIds, selectedNiveaux]);

  // ==================== LOAD FILTERS ====================

  useEffect(() => {
    const loadFilters = async () => {
      setFiltersLoading(true);
      setFiltersError(null);
      try {
        const data = await getAdminGradeFilters();
        setFiltersData(data);

        const currentYear = data.academicYears.find((ay) => ay.isCurrent);
        if (currentYear) {
          setSelectedAcademicYearId(currentYear.id);
        } else if (data.academicYears.length > 0) {
          setSelectedAcademicYearId(data.academicYears[0].id);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Erreur lors du chargement des filtres';
        setFiltersError(message);
      } finally {
        setFiltersLoading(false);
      }
    };

    loadFilters();
  }, []);

  // ==================== LOAD ANALYTICS ====================

  const loadAnalytics = useCallback(async () => {
    if (!selectedAcademicYearId) return;

    setAnalyticsLoading(true);
    try {
      const data = await getGradeAnalytics(currentFilters);
      setAnalytics(data);
    } catch (err) {
      console.error('Failed to load analytics:', err);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [currentFilters, selectedAcademicYearId]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  // ==================== LOAD TAB DATA ====================

  const loadOverviewTab = useCallback(async () => {
    if (!selectedAcademicYearId) return;

    setMatiereLoading(true);
    setOverviewLoading(true);
    setDataError(null);
    try {
      const [matiere, overview] = await Promise.all([
        getMatiereSummary(currentFilters),
        getGradeOverview(currentFilters),
      ]);
      setMatiereData(matiere);
      setOverviewData(overview);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur lors du chargement';
      setDataError(message);
    } finally {
      setMatiereLoading(false);
      setOverviewLoading(false);
    }
  }, [currentFilters, selectedAcademicYearId]);

  const loadStudentsData = useCallback(async () => {
    if (!selectedAcademicYearId) return;

    setStudentsLoading(true);
    setDataError(null);
    try {
      const data = await getStudentStatusReport(currentFilters);
      data.sort((a, b) => a.successRate - b.successRate);
      setStudentsData(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur lors du chargement';
      setDataError(message);
    } finally {
      setStudentsLoading(false);
    }
  }, [currentFilters, selectedAcademicYearId]);

  // Lazy load when tab changes
  useEffect(() => {
    switch (activeTab) {
      case 'averages':
        if (matiereData === null) loadOverviewTab();
        break;
      case 'students':
        if (studentsData === null) loadStudentsData();
        break;
    }
  }, [activeTab, matiereData, studentsData, loadOverviewTab, loadStudentsData]);

  // Clear cached data when filters change
  useEffect(() => {
    setMatiereData(null);
    setOverviewData(null);
    setStudentsData(null);
    setDataError(null);
    setExpandedMatiereKey(null);
    setExpandedStudentId(null);
    setStudentDetail([]);
  }, [selectedCampusIds, selectedAcademicYearId, selectedSemesterNumbers, selectedFiliereIds, selectedNiveaux]);

  // Reset expand state on tab change
  useEffect(() => {
    setExpandedMatiereKey(null);
    setExpandedStudentId(null);
    setStudentDetail([]);
  }, [activeTab]);

  // ==================== SEARCH FILTERING ====================

  // Accent-insensitive matching: "mathematiques" must find "Mathématiques"
  const normalize = (s: string | null | undefined): string =>
    (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

  const filteredMatiereData = useMemo(() => {
    if (!matiereData) return [];
    if (!searchQuery.trim()) return matiereData;
    const q = normalize(searchQuery);
    return matiereData.filter(
      (row) =>
        normalize(row.moduleElementName).includes(q) ||
        normalize(row.moduleName).includes(q) ||
        normalize(row.professorName).includes(q) ||
        normalize(row.groupeName).includes(q)
    );
  }, [matiereData, searchQuery]);

  const filteredStudentsData = useMemo(() => {
    if (!studentsData) return [];
    if (!searchQuery.trim()) return studentsData;
    const q = normalize(searchQuery);
    return studentsData.filter(
      (row) =>
        normalize(row.studentName).includes(q) ||
        normalize(row.studentCode).includes(q)
    );
  }, [studentsData, searchQuery]);

  // ==================== EXPAND HANDLERS ====================

  const handleToggleMatiereExpand = (moduleElementId: string, groupeId: string) => {
    const key = `${moduleElementId}|${groupeId}`;
    setExpandedMatiereKey(expandedMatiereKey === key ? null : key);
  };

  const handleToggleStudentExpand = async (studentId: string) => {
    if (expandedStudentId === studentId) {
      setExpandedStudentId(null);
      setStudentDetail([]);
      return;
    }

    setExpandedStudentId(studentId);
    setDetailLoading(true);
    try {
      const detail = await getStudentGradeDetail(studentId, currentFilters);
      setStudentDetail(detail);
    } catch (err) {
      console.error('Failed to load student detail:', err);
      setStudentDetail([]);
    } finally {
      setDetailLoading(false);
    }
  };

  // Get students for expanded matiere (filtered from overview data)
  const expandedMatiereStudents = useMemo(() => {
    if (!expandedMatiereKey || !overviewData) return [];
    const [meId, gId] = expandedMatiereKey.split('|');
    return overviewData.filter(
      (row) => row.moduleElementId === meId && row.groupeId === gId
    );
  }, [expandedMatiereKey, overviewData]);

  // ==================== OVERRIDE HANDLERS ====================

  const handleOpenOverride = (row: GradeOverviewRow) => {
    setOverrideTarget({
      studentId: row.studentId,
      groupeId: row.groupeId,
      studentName: row.studentName,
      moduleElementName: row.moduleElementName,
      components: row.components.map((c) => ({
        configId: c.configId,
        studentGradeId: c.studentGradeId,
        name: c.name,
        code: c.code,
        score: c.score,
        maxScore: c.maxScore,
        isFromRattrapage: c.isFromRattrapage,
        originalScore: c.originalScore,
      })),
    });
  };

  const handleOpenOverrideFromDetail = (studentId: string, studentName: string, detail: StudentGradeDetail) => {
    setOverrideTarget({
      studentId,
      groupeId: detail.groupeId,
      studentName,
      moduleElementName: detail.moduleElementName,
      components: detail.components.map((c) => ({
        configId: c.configId,
        studentGradeId: c.studentGradeId,
        name: c.name,
        code: c.code,
        score: c.score,
        maxScore: c.maxScore,
        isFromRattrapage: c.isFromRattrapage,
        originalScore: c.originalScore,
      })),
    });
  };

  const handleOverrideSuccess = () => {
    // Reset data to trigger re-fetch
    setMatiereData(null);
    setOverviewData(null);
    setStudentsData(null);
    setExpandedMatiereKey(null);
    setExpandedStudentId(null);
    setStudentDetail([]);
    loadAnalytics();
  };

  // ==================== ANALYTICS CARDS ====================

  const analyticsCards = [
    {
      label: 'Taux de Reussite',
      value: `${analytics?.overallSuccessRate ?? 0}%`,
      icon: TrendingUp,
      color: 'text-green-600',
      bg: 'bg-green-50',
    },
    {
      label: 'Moyenne Generale',
      value: analytics?.overallAverage?.toFixed(2) ?? '-',
      icon: BarChart3,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
    },
    {
      label: 'Rattrapages',
      value: String(analytics?.totalRattrapages ?? 0),
      icon: RotateCcw,
      color: 'text-amber-600',
      bg: 'bg-amber-50',
    },
    {
      label: 'Echecs',
      value: String(analytics?.totalFailures ?? 0),
      icon: XCircle,
      color: 'text-red-600',
      bg: 'bg-red-50',
    },
  ];

  // ==================== LOADING STATE ====================

  if (filtersLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (filtersError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">Erreur de chargement</h3>
          <p className="mt-2 text-sm text-gray-500">{filtersError}</p>
        </div>
      </div>
    );
  }

  // ==================== RENDER ====================

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-indigo-100 rounded-xl">
            <GraduationCap className="w-8 h-8 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Notes et Rattrapages</h1>
            <p className="text-gray-500">Tableau de bord des notes, resultats et rattrapages</p>
          </div>
        </div>
        {canManage && (
          <div className="flex items-center gap-3">
            <button
              onClick={() => setGradeTrackingOpen(true)}
              className="group flex items-center gap-2.5 px-5 py-2.5 text-indigo-700 font-medium rounded-xl bg-indigo-50 border border-indigo-200 hover:bg-indigo-100 hover:border-indigo-300 shadow-sm hover:shadow transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
            >
              <BookOpen className="w-4 h-4" />
              Suivi des Notes
            </button>
            <button
              onClick={() => setExportModalOpen(true)}
              className="group flex items-center gap-2.5 px-5 py-2.5 text-white font-medium rounded-xl bg-gradient-to-r from-indigo-500 to-blue-600 hover:from-indigo-600 hover:to-blue-700 shadow-md hover:shadow-lg transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
            >
              <Download className="w-4 h-4 transition-transform duration-200 group-hover:-translate-y-0.5" />
              Exporter
            </button>
          </div>
        )}
      </div>

      {/* Filter Bar */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {/* Campus - Multi-select */}
          <MultiSelectFilter
            label="Campus"
            allLabel="Tous les campus"
            options={(filtersData?.campuses ?? []).map((c) => ({ value: c.id, label: c.name }))}
            selected={selectedCampusIds}
            onChange={setSelectedCampusIds}
          />

          {/* Academic Year - Single select */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Annee Academique</label>
            <select
              value={selectedAcademicYearId}
              onChange={(e) => setSelectedAcademicYearId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {filtersData?.academicYears.map((ay) => (
                <option key={ay.id} value={ay.id}>
                  {ay.name} {ay.isCurrent ? '(En cours)' : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Semester - Multi-select */}
          <MultiSelectFilter
            label="Semestre"
            allLabel="Tous les semestres"
            options={(filtersData?.semesters ?? []).map((s) => ({ value: String(s.number), label: s.label }))}
            selected={selectedSemesterNumbers.map(String)}
            onChange={(vals) => setSelectedSemesterNumbers(vals.map(Number))}
          />

          {/* Filiere - Multi-select */}
          <MultiSelectFilter
            label="Filiere"
            allLabel="Toutes les filieres"
            options={(filtersData?.filieres ?? []).map((f) => ({ value: f.id, label: `${f.code} - ${f.name}` }))}
            selected={selectedFiliereIds}
            onChange={setSelectedFiliereIds}
          />

          {/* Niveau - Multi-select */}
          <MultiSelectFilter
            label="Niveau"
            allLabel="Tous les niveaux"
            options={(filtersData?.niveaux ?? []).map((n) => ({ value: String(n), label: `Niveau ${n}` }))}
            selected={selectedNiveaux.map(String)}
            onChange={(vals) => setSelectedNiveaux(vals.map(Number))}
          />
        </div>
      </div>

      {/* Analytics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {analyticsCards.map((card) => {
          const Icon = card.icon;
          return (
            <div key={card.label} className={`${card.bg} rounded-xl border border-gray-200 p-4 shadow-sm`}>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-white/60">
                  <Icon className={`w-5 h-5 ${card.color}`} />
                </div>
                <div>
                  <p className="text-sm text-gray-600">{card.label}</p>
                  <p className={`text-xl font-bold ${card.color}`}>
                    {analyticsLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : card.value}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Error */}
      {dataError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700">{dataError}</p>
        </div>
      )}

      {/* Tab Bar + Search */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="inline-flex border-b border-gray-200">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {/* ============ TAB: MOY. PAR CLASSE (By Matiere, expandable) ============ */}
        {activeTab === 'averages' && (
          <>
            {matiereLoading ? (
              <LoadingSpinner />
            ) : filteredMatiereData.length === 0 ? (
              <EmptyState
                icon={BookOpen}
                title="Aucune matiere trouvee"
                subtitle={searchQuery ? 'Essayez de modifier votre recherche' : 'Aucune donnee disponible pour ces filtres'}
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Matiere</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Module</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Groupe</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Professeur</th>
                      <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Etudiants</th>
                      <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Moy.</th>
                      <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Reussite</th>
                      <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">V</th>
                      <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">E</th>
                      <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">R</th>
                      <th className="px-3 py-3 w-10"></th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredMatiereData.map((matiere) => {
                      const key = `${matiere.moduleElementId}|${matiere.groupeId}`;
                      const isExpanded = expandedMatiereKey === key;

                      return (
                        <React.Fragment key={key}>
                          {/* Matiere summary row */}
                          <tr
                            className="hover:bg-gray-50 cursor-pointer transition-colors"
                            onClick={() => handleToggleMatiereExpand(matiere.moduleElementId, matiere.groupeId)}
                          >
                            <td className="px-4 py-3">
                              <div className="text-sm font-medium text-gray-900">{matiere.moduleElementName}</div>
                            </td>
                            <td className="px-3 py-3 text-sm text-gray-500">{matiere.moduleName}</td>
                            <td className="px-3 py-3 text-sm text-gray-700">{matiere.groupeName}</td>
                            <td className="px-3 py-3 text-sm text-gray-700">{matiere.professorName}</td>
                            <td className="px-3 py-3 text-center text-sm text-gray-700">
                              {matiere.gradedStudents}/{matiere.totalStudents}
                            </td>
                            <td className="px-3 py-3 text-center">
                              <span className={`text-sm font-semibold ${getAverageColor(matiere.classAverage)}`}>
                                {matiere.classAverage !== null ? matiere.classAverage.toFixed(2) : '-'}
                              </span>
                            </td>
                            <td className="px-3 py-3 text-center">
                              <span className={`text-sm font-medium ${
                                matiere.passRate >= 80 ? 'text-green-600' :
                                matiere.passRate >= 50 ? 'text-amber-600' :
                                'text-red-600'
                              }`}>
                                {matiere.passRate}%
                              </span>
                            </td>
                            <td className="px-3 py-3 text-center text-sm font-medium text-green-600">{matiere.passCount}</td>
                            <td className="px-3 py-3 text-center text-sm font-medium text-red-600">{matiere.failCount}</td>
                            <td className="px-3 py-3 text-center text-sm font-medium text-amber-600">{matiere.rattrapageCount}</td>
                            <td className="px-3 py-3 text-center">
                              {isExpanded ? (
                                <ChevronUp className="w-4 h-4 text-gray-400" />
                              ) : (
                                <ChevronDown className="w-4 h-4 text-gray-400" />
                              )}
                            </td>
                          </tr>

                          {/* Expanded student rows */}
                          {isExpanded && (
                            <tr>
                              <td colSpan={11} className="px-0 py-0">
                                <div className="bg-blue-50 border-t border-b border-blue-100">
                                  {overviewLoading ? (
                                    <div className="flex items-center justify-center py-6">
                                      <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                                      <span className="ml-2 text-sm text-blue-600">Chargement...</span>
                                    </div>
                                  ) : expandedMatiereStudents.length === 0 ? (
                                    <div className="flex items-center justify-center py-6 text-gray-500">
                                      <Users className="w-5 h-5 mr-2 text-gray-400" />
                                      <span className="text-sm">Aucun etudiant pour cette matiere</span>
                                    </div>
                                  ) : (
                                    <div className="px-6 py-4">
                                      <h4 className="text-sm font-semibold text-blue-800 mb-3">Etudiants inscrits</h4>
                                      <table className="min-w-full divide-y divide-blue-200 bg-white rounded-lg overflow-hidden">
                                        <thead className="bg-blue-100">
                                          <tr>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-blue-700 uppercase">Etudiant</th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-blue-700 uppercase">Code</th>
                                            {expandedMatiereStudents[0]?.components.map((c) => (
                                              <th key={c.code} className="px-3 py-2 text-center text-xs font-medium text-blue-700 uppercase">{c.name}</th>
                                            ))}
                                            <th className="px-3 py-2 text-center text-xs font-medium text-blue-700 uppercase">Moy.</th>
                                            <th className="px-3 py-2 text-center text-xs font-medium text-blue-700 uppercase">Statut</th>
                                            {canManage && <th className="px-3 py-2 text-center text-xs font-medium text-blue-700 uppercase">Actions</th>}
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-blue-100">
                                          {expandedMatiereStudents.map((row, idx) => {
                                            const badge = getStatusBadge(row.status);
                                            return (
                                              <tr key={`${row.studentId}-${idx}`} className="hover:bg-blue-50">
                                                <td className="px-4 py-2 text-sm font-medium text-gray-900">{row.studentName}</td>
                                                <td className="px-3 py-2 text-sm text-gray-500">{row.studentCode}</td>
                                                {row.components.map((comp) => (
                                                  <td key={comp.code} className="px-3 py-2 text-center text-sm">
                                                    <div className="inline-flex items-center gap-1">
                                                      <span className={comp.score !== null ? 'text-gray-900' : 'text-gray-400'}>
                                                        {comp.score !== null ? comp.score : '-'}
                                                      </span>
                                                      {comp.isFromRattrapage && (
                                                        <span
                                                          className="inline-flex items-center justify-center w-4 h-4 text-[10px] font-bold bg-amber-100 text-amber-700 rounded-full cursor-help"
                                                          title={`Score original: ${comp.originalScore !== null ? comp.originalScore : 'N/A'}`}
                                                        >
                                                          R
                                                        </span>
                                                      )}
                                                      {comp.hasOverride && (
                                                        <span
                                                          className="inline-flex items-center justify-center w-4 h-4 text-[10px] font-bold bg-blue-100 text-blue-700 rounded-full cursor-help"
                                                          title="Note modifiee par un administrateur"
                                                        >
                                                          M
                                                        </span>
                                                      )}
                                                    </div>
                                                  </td>
                                                ))}
                                                <td className="px-3 py-2 text-center">
                                                  <span className={`text-sm font-semibold ${getAverageColor(row.weightedAverage)}`}>
                                                    {row.weightedAverage !== null ? row.weightedAverage.toFixed(2) : '-'}
                                                  </span>
                                                </td>
                                                <td className="px-3 py-2 text-center">
                                                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badge.classes}`}>
                                                    {badge.label}
                                                  </span>
                                                </td>
                                                {canManage && (
                                                  <td className="px-3 py-2 text-center">
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleOpenOverride(row);
                                                      }}
                                                      className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                                                    >
                                                      <Edit3 className="h-3 w-3" />
                                                      Modifier
                                                    </button>
                                                  </td>
                                                )}
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Row count footer */}
            {matiereData !== null && !matiereLoading && (
              <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 text-xs text-gray-500">
                {filteredMatiereData.length} matiere{filteredMatiereData.length !== 1 ? 's' : ''}
              </div>
            )}
          </>
        )}

        {/* ============ TAB: STUDENTS (By Student) ============ */}
        {activeTab === 'students' && (
          <>
            {studentsLoading ? (
              <LoadingSpinner />
            ) : filteredStudentsData.length === 0 ? (
              <EmptyState
                icon={Users}
                title="Aucun etudiant trouve"
                subtitle={searchQuery ? 'Essayez de modifier votre recherche' : 'Aucune donnee disponible pour ces filtres'}
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Etudiant</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Filiere</th>
                      <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Niveau</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Groupe</th>
                      <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Valides</th>
                      <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Echoues</th>
                      <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Ratt.</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Moyenne</th>
                      <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Taux</th>
                      <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                      <th className="px-3 py-3 w-10"></th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredStudentsData.map((row) => {
                      const isExpanded = expandedStudentId === row.studentId;
                      const badge = getStudentStatusBadge(row.status);

                      return (
                        <React.Fragment key={row.studentId}>
                          {/* Student summary row */}
                          <tr
                            className="hover:bg-gray-50 cursor-pointer transition-colors"
                            onClick={() => handleToggleStudentExpand(row.studentId)}
                          >
                            <td className="px-4 py-3">
                              <div className="text-sm font-medium text-gray-900">{row.studentName}</div>
                              <div className="text-xs text-gray-500">{row.studentCode}</div>
                            </td>
                            <td className="px-3 py-3">
                              <div className="text-sm text-gray-900">{row.filiereCode}</div>
                              <div className="text-xs text-gray-500">{row.filiereName}</div>
                            </td>
                            <td className="px-3 py-3 text-center text-sm text-gray-700">{row.niveau}</td>
                            <td className="px-3 py-3 text-sm text-gray-700">{row.groupeName}</td>
                            <td className="px-3 py-3 text-center">
                              <span className="text-sm font-medium text-green-600">{row.modulesPassed}</span>
                              <span className="text-xs text-gray-400">/{row.totalModules}</span>
                            </td>
                            <td className="px-3 py-3 text-center">
                              <span className={`text-sm font-medium ${row.modulesFailed > 0 ? 'text-red-600' : 'text-gray-500'}`}>
                                {row.modulesFailed}
                              </span>
                            </td>
                            <td className="px-3 py-3 text-center">
                              <span className={`text-sm font-medium ${row.modulesInRattrapage > 0 ? 'text-amber-600' : 'text-gray-500'}`}>
                                {row.modulesInRattrapage}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={`text-sm font-semibold ${getAverageColor(row.overallAverage)}`}>
                                {row.overallAverage !== null ? row.overallAverage.toFixed(2) : '-'}
                              </span>
                            </td>
                            <td className="px-3 py-3 text-center">
                              <span className={`text-sm font-medium ${
                                row.successRate >= 80 ? 'text-green-600' :
                                row.successRate >= 50 ? 'text-amber-600' :
                                'text-red-600'
                              }`}>
                                {row.successRate}%
                              </span>
                            </td>
                            <td className="px-3 py-3 text-center">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.classes}`}>
                                {badge.label}
                              </span>
                            </td>
                            <td className="px-3 py-3 text-center">
                              <div className="flex items-center justify-center gap-1">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setStudentReportModal({
                                      open: true,
                                      studentId: row.studentId,
                                      studentName: row.studentName,
                                      studentCode: row.studentCode,
                                    });
                                  }}
                                  className="p-1 rounded hover:bg-indigo-50 transition-colors"
                                  title="Rapport individuel"
                                >
                                  <FileText className="w-4 h-4 text-indigo-400 hover:text-indigo-600" />
                                </button>
                                {isExpanded ? (
                                  <ChevronUp className="w-4 h-4 text-gray-400" />
                                ) : (
                                  <ChevronDown className="w-4 h-4 text-gray-400" />
                                )}
                              </div>
                            </td>
                          </tr>

                          {/* Expanded matiere details */}
                          {isExpanded && (
                            <tr>
                              <td colSpan={11} className="px-0 py-0">
                                <div className="bg-blue-50 border-t border-b border-blue-100">
                                  {detailLoading ? (
                                    <div className="flex items-center justify-center py-6">
                                      <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                                      <span className="ml-2 text-sm text-blue-600">Chargement du detail...</span>
                                    </div>
                                  ) : studentDetail.length === 0 ? (
                                    <div className="flex items-center justify-center py-6 text-gray-500">
                                      <BookOpen className="w-5 h-5 mr-2 text-gray-400" />
                                      <span className="text-sm">Aucun detail disponible</span>
                                    </div>
                                  ) : (
                                    <div className="px-6 py-4">
                                      <h4 className="text-sm font-semibold text-blue-800 mb-3">Detail par matiere</h4>
                                      <div className="space-y-3">
                                        {studentDetail.map((detail) => {
                                          const detailBadge = getStatusBadge(detail.status);
                                          return (
                                            <div key={detail.moduleElementId} className="bg-white rounded-lg border border-blue-200 p-3">
                                              <div className="flex items-center justify-between mb-2">
                                                <div>
                                                  <div className="text-sm font-medium text-gray-900">{detail.moduleElementName}</div>
                                                  <div className="text-xs text-gray-500">{detail.moduleName}</div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                  <span className={`text-sm font-semibold ${getAverageColor(detail.weightedAverage)}`}>
                                                    {detail.weightedAverage !== null ? detail.weightedAverage.toFixed(2) : '-'}
                                                  </span>
                                                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${detailBadge.classes}`}>
                                                    {detailBadge.label}
                                                  </span>
                                                  {canManage && (
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleOpenOverrideFromDetail(row.studentId, row.studentName, detail);
                                                      }}
                                                      className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                                                    >
                                                      <Edit3 className="h-3 w-3" />
                                                      Modifier
                                                    </button>
                                                  )}
                                                </div>
                                              </div>
                                              {/* Component scores */}
                                              <div className="flex flex-wrap gap-3 text-xs">
                                                {detail.components.map((comp) => (
                                                  <div key={comp.code} className="flex items-center gap-1 bg-gray-50 rounded px-2 py-1">
                                                    <span className="text-gray-500">{comp.name}:</span>
                                                    <span className={comp.score !== null ? 'font-medium text-gray-900' : 'text-gray-400'}>
                                                      {comp.score !== null ? comp.score : '-'}
                                                    </span>
                                                    <span className="text-gray-400">/{comp.maxScore}</span>
                                                    {comp.isFromRattrapage && (
                                                      <span
                                                        className="inline-flex items-center justify-center w-3.5 h-3.5 text-[9px] font-bold bg-amber-100 text-amber-700 rounded-full cursor-help"
                                                        title={`Score original: ${comp.originalScore !== null ? comp.originalScore : 'N/A'}`}
                                                      >
                                                        R
                                                      </span>
                                                    )}
                                                    {comp.hasOverride && (
                                                      <span
                                                        className="inline-flex items-center justify-center w-3.5 h-3.5 text-[9px] font-bold bg-blue-100 text-blue-700 rounded-full cursor-help"
                                                        title="Note modifiee"
                                                      >
                                                        M
                                                      </span>
                                                    )}
                                                  </div>
                                                ))}
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Row count footer */}
            {studentsData !== null && !studentsLoading && (
              <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 text-xs text-gray-500">
                {filteredStudentsData.length} etudiant{filteredStudentsData.length !== 1 ? 's' : ''}
              </div>
            )}
          </>
        )}

      </div>

      {/* Grade Override Modal */}
      {overrideTarget && (
        <GradeOverrideModal
          isOpen={!!overrideTarget}
          onClose={() => setOverrideTarget(null)}
          studentId={overrideTarget.studentId}
          groupeId={overrideTarget.groupeId}
          studentName={overrideTarget.studentName}
          moduleElementName={overrideTarget.moduleElementName}
          components={overrideTarget.components}
          onOverrideSuccess={handleOverrideSuccess}
        />
      )}

      {/* Export Modal */}
      <GradeExportModal
        isOpen={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        filters={currentFilters}
        filterSummary={filterSummary}
      />

      {/* Student Report Modal */}
      <StudentGradeReportModal
        isOpen={studentReportModal.open}
        onClose={() => setStudentReportModal({ open: false, studentId: '', studentName: '', studentCode: '' })}
        studentId={studentReportModal.studentId}
        studentName={studentReportModal.studentName}
        studentCode={studentReportModal.studentCode}
        filters={currentFilters}
      />

      {/* Grade Tracking Modal (Suivi des Notes) */}
      <GradeTrackingModal
        isOpen={gradeTrackingOpen}
        onClose={() => setGradeTrackingOpen(false)}
        filters={{
          academicYearId: selectedAcademicYearId,
          campusIds: selectedCampusIds,
          filiereIds: selectedFiliereIds,
          niveaux: selectedNiveaux,
        }}
      />
    </div>
  );
};

// ==================== SUB-COMPONENTS ====================

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
    </div>
  );
}

interface EmptyStateProps {
  icon: React.FC<{ className?: string }>;
  title: string;
  subtitle: string;
}

function EmptyState({ icon: Icon, title, subtitle }: EmptyStateProps) {
  return (
    <div className="text-center py-16">
      <Icon className="mx-auto h-12 w-12 text-gray-400" />
      <h3 className="mt-4 text-lg font-medium text-gray-900">{title}</h3>
      <p className="mt-2 text-sm text-gray-500">{subtitle}</p>
    </div>
  );
}

export default GradesDashboardPage;
